import { useState, useEffect, useRef, useCallback } from "react";

const useTimer = (initialSeconds = 0, options = {}) => {
  const {
    onComplete = () => {},
    onTick = () => {},
    autoStart = false,
    step = 1,
  } = options;

  const [seconds, setSeconds] = useState(initialSeconds);
  const [isActive, setIsActive] = useState(autoStart);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const initialSecondsRef = useRef(initialSeconds);

  // Update initial seconds ref when prop changes
  useEffect(() => {
    initialSecondsRef.current = initialSeconds;
    setSeconds(initialSeconds);
  }, [initialSeconds]);

  // Timer logic
  useEffect(() => {
    if (isActive && !isPaused && seconds > 0) {
      intervalRef.current = setInterval(() => {
        setSeconds((prevSeconds) => {
          const newSeconds = prevSeconds - step;
          onTick(newSeconds);

          if (newSeconds <= 0) {
            setIsActive(false);
            onComplete();
            return 0;
          }

          return newSeconds;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isActive, isPaused, seconds, step, onTick, onComplete]);

  // Start the timer
  const start = useCallback(() => {
    if (seconds > 0) {
      setIsActive(true);
      setIsPaused(false);
    }
  }, [seconds]);

  // Pause the timer
  const pause = useCallback(() => {
    setIsPaused(true);
  }, []);

  // Resume the timer
  const resume = useCallback(() => {
    if (isActive) {
      setIsPaused(false);
    }
  }, [isActive]);

  // Stop the timer
  const stop = useCallback(() => {
    setIsActive(false);
    setIsPaused(false);
    clearInterval(intervalRef.current);
  }, []);

  // Reset the timer to initial value
  const reset = useCallback((autStart = false) => {
    setSeconds(initialSecondsRef.current);
    setIsActive(autStart);
    setIsPaused(false);
    clearInterval(intervalRef.current);
  }, []);

  // Set custom seconds
  const setCustomSeconds = useCallback((newSeconds) => {
    setSeconds(newSeconds);
    initialSecondsRef.current = newSeconds;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
    };
  }, []);

  return {
    seconds,
    isActive,
    isPaused,
    isCompleted: seconds === 0 && !isActive,
    start,
    pause,
    resume,
    stop,
    reset,
    setSeconds: setCustomSeconds,
  };
};

export default useTimer;
